import React, { useCallback, useState } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { Card, CardHeader, CardBody } from "reactstrap";
import { utils } from "../../../../utils/utils";
import { sequenceApi } from "../../../../services/sequenceServices";
import { useAuth } from "../../../../providers/authProvider";
import ConfirmationModal from "../../../ConfirmationModal";
import InformationModal from "../../../InformationModal";
import SequenceDetailsModal from "../SequenceDetailsModal";
import DraggableSequenceRow, { SequenceRowCells } from "./DraggableSequenceRow";

const DraggableSequenceSection = ({
  title,
  sequences: initialSequences,
  onReorder,
  onStatusChange,
}) => {
  const [authContext] = useAuth();
  const [sequences, setSequences] = useState(initialSequences);
  const [saving, setSaving] = useState(false);
  const [activeSequence, setActiveSequence] = useState(null);

  const [detailModal, setDetailModal] = useState(false);
  const [sequenceSelected, setSequenceSelected] = useState(null);
  const [detailerId, setDetailerId] = useState(null);
  const [checkerId, setCheckerId] = useState(null);
  const [sequenceStatusId, setSequenceStatusId] = useState(null);
  const [informationModal, setInformationModal] = useState(
    utils.initInformationModal,
  );
  const [confirmationModal, setConfirmationModal] = useState(
    utils.initConfirmationModal,
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  React.useEffect(() => {
    setSequences(initialSequences);
  }, [initialSequences]);

  const onClear = useCallback(() => {
    setDetailModal(false);
    setSequenceSelected(null);
    setDetailerId(null);
    setCheckerId(null);
    setSequenceStatusId(null);
    setConfirmationModal(utils.initConfirmationModal);
    setInformationModal(utils.initInformationModal);
  }, []);

  const handleStatusAction = useCallback(
    (sequence, nextStatusId) => {
      setConfirmationModal({
        isOpen: true,
        onSubmit: async () => {
          try {
            const { detailer, checker, ...sequenceFields } = sequence;
            await sequenceApi.updateSequence(sequence.id, {
              ...sequenceFields,
              detailerId: sequence.detailer?.id ?? null,
              checkerId: sequence.checker?.id ?? null,
              sequenceStatusId: nextStatusId,
            });
            onClear();
            setInformationModal({
              isOpen: true,
              title: "Sequence Updated",
              body: "Sequence status updated successfully.",
            });
            if (onStatusChange) onStatusChange();
          } catch {
            onClear();
            setInformationModal({
              isOpen: true,
              title: "Error",
              body: "There was an error with your request.",
            });
          }
        },
        onClose: onClear,
        title: "Update Sequence Status",
        body: `<div class="text-center">Do you confirm you want to update the status of sequence <strong>${sequence.job?.jobNumber}</strong>?</div>`,
        confirmColor: "warning",
      });
    },
    [onClear, onStatusChange],
  );

  const handleDetails = useCallback((sequence) => {
    setSequenceSelected(sequence);
    setDetailerId(sequence.detailer?.id || null);
    setCheckerId(sequence.checker?.id || null);
    setSequenceStatusId(sequence.sequenceStatusId);
    setDetailModal(true);
  }, []);

  const onSubmitDetails = async (e) => {
    e.preventDefault();
    try {
      const { detailer, checker, ...sequenceFields } = sequenceSelected;
      await sequenceApi.updateSequence(sequenceSelected.id, {
        ...sequenceFields,
        detailerId: detailerId ?? null,
        checkerId: checkerId ?? null,
        sequenceStatusId,
      });
      onClear();
      setInformationModal({
        isOpen: true,
        title: "Sequence Updated",
        body: "Sequence updated successfully.",
      });
      if (onStatusChange) onStatusChange();
    } catch {
      onClear();
      setInformationModal({
        isOpen: true,
        title: "Error",
        body: "There was an error with your request.",
      });
    }
  };

  const handleDragStart = useCallback(
    ({ active }) => {
      setActiveSequence(sequences.find((s) => s.id === active.id) ?? null);
    },
    [sequences],
  );

  const handleDragEnd = useCallback(
    async (event) => {
      setActiveSequence(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = sequences.findIndex((s) => s.id === active.id);
      const newIndex = sequences.findIndex((s) => s.id === over.id);
      const reordered = arrayMove(sequences, oldIndex, newIndex).map(
        (seq, idx) => ({ ...seq, priority: idx + 1 }),
      );
      setSequences(reordered);

      const priorities = reordered.map((seq, idx) => ({
        id: seq.id,
        priority: idx + 1,
      }));

      try {
        setSaving(true);
        await sequenceApi.updatePriorities(priorities);
        if (onReorder) onReorder(reordered);
      } catch {
        setSequences(sequences);
      } finally {
        setSaving(false);
      }
    },
    [sequences, onReorder],
  );

  const handleDragCancel = useCallback(() => {
    setActiveSequence(null);
  }, []);

  return (
    <>
      <div>
        <Card className="box-shadow-none border-left border-right mb-0 w-100">
          <CardHeader className="pb-0 d-flex align-items-center justify-content-between">
            <div className="h4 m-0">{title}</div>
            {saving && <small className="text-muted">Saving priority...</small>}
          </CardHeader>
          <CardBody className="pt-0">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <div className="col-12 px-0 overflow-x-auto">
                <table className="table table-striped mb-0">
                  <thead className="text-muted small">
                    <tr>
                      <th>Job#</th>
                      <th>Customer</th>
                      <th>Sequence Name</th>
                      <th>Rev Date</th>
                      <th>PM Notes</th>
                      <th>Detailer</th>
                      <th>Checker</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={sequences.map((s) => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody>
                      {sequences.length ? (
                        sequences.map((seq, idx) => (
                          <DraggableSequenceRow
                            key={seq.id}
                            sequence={seq}
                            isTop={idx === 0}
                            onStatusAction={handleStatusAction}
                            onDetails={handleDetails}
                          />
                        ))
                      ) : (
                        <tr>
                          <td
                            className="text-muted small text-center"
                            colSpan={8}
                          >
                            No data to show
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </SortableContext>
                </table>
              </div>

              {/* DragOverlay renders the dragged row outside the DOM flow — eliminates flickering */}
              <DragOverlay>
                {activeSequence ? (
                  <table
                    className="table table-striped mb-0"
                    style={{ width: "100%", opacity: 0.9 }}
                  >
                    <tbody>
                      <tr
                        style={{
                          cursor: "grabbing",
                          backgroundColor: "#e8f4fd",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        }}
                      >
                        <SequenceRowCells
                          sequence={activeSequence}
                          isTop={false}
                        />
                      </tr>
                    </tbody>
                  </table>
                ) : null}
              </DragOverlay>
            </DndContext>
          </CardBody>
        </Card>
      </div>

      {informationModal.isOpen ? (
        <InformationModal
          title={informationModal.title}
          body={informationModal.body}
          onClose={onClear}
        />
      ) : confirmationModal.isOpen ? (
        <ConfirmationModal {...confirmationModal} />
      ) : detailModal && sequenceSelected ? (
        <SequenceDetailsModal
          sequence={sequenceSelected}
          detailModal={detailModal}
          onSubmit={onSubmitDetails}
          onClear={onClear}
          checkerId={checkerId}
          setCheckerId={setCheckerId}
          detailerId={detailerId}
          setDetailerId={setDetailerId}
          enableUpdate={utils.isAdmin(authContext.currentUser?.role?.id)}
          statusId={sequenceStatusId}
          setStatusId={setSequenceStatusId}
        />
      ) : null}
    </>
  );
};

export default DraggableSequenceSection;
